import React, { useState, useEffect, useCallback } from "react";
import { Loader, Plus, X, Zap } from "react-feather";
import { SavedDescriptions } from "./SavedDecriptions";
import {
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { LocationSearch } from "./LocationSearch";
import { PostType } from "./PostType";
import { Feed, TypeMapping } from "../../types/api/Feed";
import { Location } from "../../types/api/Location";
import { ContentCheck, validateText } from "./ContentCheck";
import { AddFiles, SelectedFile } from "../../types/PostSelection";
import { SavedContent } from "../../types/api/SavedContent";
import { handleGenerateHashtags as generateHashtags } from "../../api/hashtags";
import { generateCaptions } from "../../api/captions";
import cx from "classnames";
import { formatLocation } from "../../shared/formatLocation";
import { HashtagList } from "../HashtagList";
import { postWithLocationAndHashtags } from "../PostDisplay";
import { useProfile } from "../../state/db/profile";
import { useSavedDescriptions } from "../../state/db/savedDescriptions";
import { MentionOption, MentionTextarea } from "../MentionTextarea";
import { SelectedSavedDescriptions } from "./SelectedSavedDescriptions";
import { usePositionManagement } from "./usePositionManagement";
import { useSelectedSavedDescriptions } from "../../state/hooks/useSelectedSavedDescriptions";
import { SavedDescriptionForPost } from "../../shared/formatPost";
import { RequirementsType } from "../Feed/Requirements";
import { PanoramaSlicer } from "../PanoramaSlicer";

export function PostCompose({
  title,
  register,
  watch,
  setValue,
  name,
  canAutoPublish,
  postTypes,
  typeMapping,
  feed,
  selectedFiles,
  isModalOpened,
  editFeed,
  onHashtagsGenerated,
  addFiles,
}: {
  title?: string;
  register: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  name: string;
  canAutoPublish?: boolean;
  postTypes: string[];
  typeMapping?: TypeMapping;
  feed?: Feed;
  selectedFiles?: SelectedFile[];
  isModalOpened: boolean;
  editFeed?: () => void;
  onHashtagsGenerated?: () => void;
  addFiles?: AddFiles;
}) {
  const { savedDescriptions } = useSavedDescriptions();
  const fieldName = `${name}.post_content`;
  const locationField = `${name}.location`;
  const hashtagsField = `${name}.hashtags`;
  const [open, setOpen] = useState(false);
  const [showDescriptionInput, setShowDescriptionInput] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [captions, setCaptions] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hashtags, setHashtags] = useState<string[]>(
    watch(hashtagsField) ?? []
  );
  const [isGeneratingHashtags, setIsGeneratingHashtags] = useState(false);
  const [showSavedDescriptionDropdown, setShowSavedDescriptionDropdown] =
    useState(false);
  const { profile } = useProfile();
  const { selectedSavedDescriptions, setSelectedSavedDescriptions } =
    useSelectedSavedDescriptions(
      name === "default"
        ? "selectedSavedDescriptions"
        : `selectedSavedDescriptions-${name}`
    );

  // Use custom hook for position management
  const { positions, handlers } = usePositionManagement(
    selectedSavedDescriptions
  );

  // Update the form with positions whenever they change
  useEffect(() => {
    setValue(`${name}.positions`, positions);
  }, [positions, setValue, name]);

  // Update the form with selectedSavedDescriptions whenever they change
  useEffect(() => {
    setValue(`${name}.selectedSavedDescriptions`, selectedSavedDescriptions);
  }, [selectedSavedDescriptions, setValue, name]);

  const updateHashtags = useCallback(
    (hashtags: string[]) => {
      setValue(hashtagsField, hashtags);
      setHashtags(hashtags);
    },
    [setValue, hashtagsField]
  );

  const handleRemoveHashtags = useCallback(
    (hashtagsToRemove: string[]) => {
      const currentHashtags = hashtags;
      const filteredHashtags = currentHashtags.filter(
        (hashtag) => !hashtagsToRemove.includes(hashtag)
      );
      updateHashtags(filteredHashtags);
    },
    [hashtags, updateHashtags]
  );

  // Reorder hashtags when selectedSavedDescriptions order changes
  useEffect(() => {
    if (!selectedSavedDescriptions?.length || !savedDescriptions?.length) {
      return;
    }

    // Use current hashtags from the form to avoid stale closures
    const currentHashtags = watch(hashtagsField) ?? [];
    if (currentHashtags.length === 0) {
      return;
    }

    const savedDescHashtagsMap = new Map<string, number[]>();

    // Create a map of hashtag -> saved description IDs that contain it
    savedDescriptions.forEach((desc) => {
      const descHashtags = desc.hashtags.variants[name] ?? desc.hashtags.main;
      descHashtags?.forEach((hashtag) => {
        if (!savedDescHashtagsMap.has(hashtag)) {
          savedDescHashtagsMap.set(hashtag, []);
        }
        savedDescHashtagsMap.get(hashtag)!.push(desc.id!);
      });
    });

    // Separate hashtags into those from saved descriptions and manual ones
    const hashtagsFromSavedDescs: string[] = [];
    const manualHashtags: string[] = [];

    currentHashtags.forEach((hashtag: string) => {
      const associatedIds = savedDescHashtagsMap.get(hashtag);
      // Only consider a hashtag as "from saved desc" if it comes from a SELECTED saved description
      if (associatedIds?.some((id) => selectedSavedDescriptions.includes(id))) {
        hashtagsFromSavedDescs.push(hashtag);
      } else {
        manualHashtags.push(hashtag);
      }
    });

    // Reorder hashtags from saved descriptions based on selectedSavedDescriptions order
    const reorderedSavedDescHashtags: string[] = [];
    selectedSavedDescriptions.forEach((id) => {
      const desc = savedDescriptions.find((d) => d.id === id);
      if (desc) {
        const descHashtags = desc.hashtags.variants[name] ?? desc.hashtags.main;
        descHashtags?.forEach((hashtag) => {
          if (
            hashtagsFromSavedDescs.includes(hashtag) &&
            !reorderedSavedDescHashtags.includes(hashtag)
          ) {
            reorderedSavedDescHashtags.push(hashtag);
          }
        });
      }
    });

    // Combine reordered saved desc hashtags with manual hashtags
    const newHashtagOrder = [...reorderedSavedDescHashtags, ...manualHashtags];

    // Only update if the order actually changed
    if (JSON.stringify(newHashtagOrder) !== JSON.stringify(currentHashtags)) {
      updateHashtags(newHashtagOrder);
    }
  }, [
    selectedSavedDescriptions,
    savedDescriptions,
    name,
    updateHashtags,
    watch,
    hashtagsField,
  ]);

  const formattedLocation = formatLocation(
    feed?.location_type === "EMBED" ? watch(locationField) : undefined
  );
  const savedDescriptionsForPost: SavedDescriptionForPost[] =
    savedDescriptions.map((desc) => ({
      id: desc.id!,
      slug: desc.slug!,
      content: desc.variants[name] ?? desc.content,
      position: positions.content[desc.id!] ?? desc.position ?? "INLINE",
      selected: selectedSavedDescriptions.includes(desc.id!),
      hashtags: desc.hashtags.variants[name] ?? desc.hashtags.main,
      hashtagsPosition:
        positions.hashtag[desc.id!] ?? desc.hashtag_position ?? "START",
      tagsPosition: positions.tag[desc.id!] ?? desc.tag_position ?? "START",
    }));
  const template = feed?.post_template_override ?? profile?.post_template ?? "";
  const content = postWithLocationAndHashtags(
    template,
    watch(fieldName),
    hashtags,
    formattedLocation,
    savedDescriptionsForPost
  );

  const aboveMaxLength = validateText(feed, content);

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!isModalOpened) {
      setHashtags([]);
      setShowDescriptionInput(false);
      setAdditionalInfo("");
      setShowSavedDescriptionDropdown(false);
    }
  }, [isModalOpened]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showSavedDescriptionDropdown && !target.closest(".dropdown")) {
        setShowSavedDescriptionDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSavedDescriptionDropdown]);

  useEffect(() => {
    if (selectedSavedDescriptions) {
      for (const id of selectedSavedDescriptions) {
        const desc = savedDescriptions?.find((d) => d.id === id);
        if (!desc) continue;
        const content = watch(fieldName);
        const formatted = formatSavedDescription(desc);
        if (content?.includes(formatted) || content?.includes(`+${desc.slug}`))
          continue;

        let value = content ?? "";
        const hasValue =
          (desc?.content?.trim?.() ?? "").length > 0 ||
          Object.values(desc?.variants).some(
            (v) => (v! as string).trim().length > 0
          );
        if (hasValue && desc.position === "INLINE") {
          value = `${value} ${formatted}`;
        }
        setValue(fieldName, value.trim());
      }
    }
  }, [
    selectedSavedDescriptions,
    fieldName,
    watch,
    setValue,
    feed?.slug,
    savedDescriptions,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateContent = (desc: SavedContent) => {
    setSelectedSavedDescriptions?.((prev) => {
      if (prev.includes(desc.id as number)) {
        return prev;
      }
      return [...prev, desc.id as number];
    });
  };

  const onAdd = (options: MentionOption) => {
    setSelectedSavedDescriptions?.((prev) => {
      if (prev.includes(options.id as number)) {
        return prev;
      }
      return [...prev, options.id as number];
    });
  };

  const onRemove = (options: MentionOption) => {
    setSelectedSavedDescriptions?.((prev) =>
      prev.filter((id) => id !== options.id)
    );
  };

  const handleRemoveSavedDescription = (id: number) => {
    setSelectedSavedDescriptions?.((prev) =>
      prev.filter((descId) => descId !== id)
    );
  };

  useEffect(() => {
    const formHashtags = watch(hashtagsField) ?? [];
    setHashtags(formHashtags);
  }, [watch(hashtagsField)?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLocation = (location: Location) => {
    setValue(locationField, location);
  };

  const handleGenerateHashtags = async () => {
    try {
      console.log("selectedFiles", selectedFiles);
      if (!selectedFiles?.length) {
        throw new Error("No images selected");
      }
      setIsGeneratingHashtags(true);
      const result = await generateHashtags({
        imageUrls: selectedFiles.map((file) => file.url),
        maxHashtags: 10,
        previous_hashtags: hashtags,
      });
      console.log("result", result);

      const newHashtags = [...hashtags, ...result.hashtags];
      updateHashtags(newHashtags);
      // Notify parent that hashtags were generated
      onHashtagsGenerated?.();
    } catch (error) {
      console.error("Failed to generate hashtags:", error);
    } finally {
      setIsGeneratingHashtags(false);
    }
  };

  const handleGenerateDescription = () => {
    setShowDescriptionInput(!showDescriptionInput);
  };

  const handleGenerateCaptions = async () => {
    try {
      if (!selectedFiles?.length) {
        throw new Error("No images selected");
      }
      setIsGenerating(true);
      const result = await generateCaptions({
        imageUrls: selectedFiles.map((file) => file.url),
        additionalInfo,
      });

      if (result.error) {
        throw new Error(result.error);
      }

      setCaptions(result.captions);
    } catch (error) {
      console.error("Failed to generate captions:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const userSelectedFiles = watch(`${name}.selected_files`);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-2">
        <div className="prose">{title ? <h2>{title}</h2> : <h2>Post</h2>}</div>

        {canAutoPublish && (
          <div className="form-control flex flex-row items-center justify-start">
            <label className="label">
              Auto-Publish
              <input
                type="checkbox"
                id={`${name}-auto-publish`}
                className="toggle"
                {...register(`${name}.auto_publish`)}
              />
            </label>
          </div>
        )}
      </div>

      <div className="card bg-base-100">
        <div className="card-body py-4 px-3 sm:px-6">
          <div className="flex justify-between">
            <div className="flex align-middle gap-4"></div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between gap-2 mb-2">
            <div className="w-full sm:w-1/2">
              <LocationSearch
                updateLocation={updateLocation}
                initialLocation={watch(locationField)}
                aboveMaxLength={!!aboveMaxLength}
                selectedFiles={selectedFiles}
              />
            </div>

            <div className="w-full sm:w-auto">
              <PostType
                name={name}
                setValue={setValue}
                watch={watch}
                postTypes={postTypes}
                typeMapping={typeMapping}
              />
            </div>
          </div>

          <ContentCheck
            feed={feed}
            selectedFiles={selectedFiles}
            userSelectedFiles={userSelectedFiles}
            postType={watch(`${name}.type`)}
            setValue={setValue}
            content={watch(fieldName)}
            hashtags={hashtags}
            location={formattedLocation}
            formattedContent={content}
          />

          {feed && addFiles && selectedFiles?.length === 1 && (
            <PanoramaSlicer
              feed={feed}
              sourceFile={selectedFiles[0]}
              watch={watch}
              setValue={setValue}
            />
          )}

          <div className="form-control mb-2">
            {/* <textarea
              placeholder="Write the post description"
              {...register(fieldName)}
              className={cx(
                "textarea w-full",
                aboveMaxLength && "textarea-error"
              )}
              rows={4}
            /> */}

            <MentionTextarea
              value={watch(fieldName) ?? ""}
              onChange={(value) => setValue(fieldName, value)}
              triggers={[
                {
                  triggerChar: "+",
                  options: (savedDescriptions || []).map((desc) => ({
                    id: desc.id,
                    label: desc.name ?? "",
                    value: desc.slug ?? "",
                  })),
                },
              ]}
              placeholder="Type @ for users, # for hashtags, ! for categories..."
              onAdd={onAdd}
              onRemove={onRemove}
            />

            <label className={cx("label", aboveMaxLength && "text-error")}>
              <span className="label-text">Character Count (Total):</span>
              <span className="label-text-alt">{content.length}</span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between gap-6">
            <HashtagList
              hashtags={hashtags}
              onHashtagsChange={updateHashtags}
              aboveMaxLength={!!aboveMaxLength}
              className="mb-4"
              content={content}
              maxContentLength={
                (feed?.requirements as RequirementsType)?.TEXT?.length
              }
            />
          </div>

          <SelectedSavedDescriptions
            selectedSavedDescriptions={selectedSavedDescriptions}
            savedDescriptions={savedDescriptions || []}
            positions={positions}
            handlers={handlers}
            hashtagsField={hashtagsField}
            setValue={setValue}
            watch={watch}
            onRemove={handleRemoveSavedDescription}
            onReorder={(newOrder) => {
              setValue(`${name}.selectedSavedDescriptions`, newOrder);
              setSelectedSavedDescriptions(() => newOrder);
            }}
            onRemoveHashtags={handleRemoveHashtags}
            currentVariant={name}
          />

          <div className="flex flex-col sm:flex-row gap-2 mb-2 items-stretch sm:items-center">
            <button
              type="button"
              className={cx(
                "btn gap-2",
                showDescriptionInput ? "btn-neutral" : "btn-primary"
              )}
              onClick={handleGenerateDescription}
            >
              {showDescriptionInput ? <X size={16} /> : <Zap size={16} />}
              <span>Generate Description</span>
            </button>

            <button
              type="button"
              className="btn gap-2 btn-primary"
              onClick={handleGenerateHashtags}
              disabled={isGeneratingHashtags}
            >
              {isGeneratingHashtags ? <Loader size={16} /> : <Zap size={16} />}
              <span>Generate Hashtags</span>
            </button>

            <div className="dropdown dropdown-top dropdown-end">
              <button
                type="button"
                tabIndex={0}
                className="btn btn-ghost gap-2 w-full sm:w-auto font-normal hover:bg-primary hover:text-slate-950"
              >
                <Plus size={16} />
                <span className="hidden sm:inline">Add saved description</span>
                <span className="sm:hidden">Add saved</span>
              </button>

              <div className="dropdown-content bg-base-100 rounded-box z-[1] w-64 shadow-lg border max-h-64 overflow-y-auto">
                <ul tabIndex={0} className="menu menu-vertical">
                  {savedDescriptions &&
                  savedDescriptions.filter(
                    (desc) => !selectedSavedDescriptions.includes(desc.id!)
                  ).length > 0 ? (
                    savedDescriptions
                      .filter(
                        (desc) => !selectedSavedDescriptions.includes(desc.id!)
                      )
                      .map((desc) => (
                        <li className="w-full" key={desc.id}>
                          <button
                            type="button"
                            className="text-left justify-start"
                            onClick={() => {
                              updateContent(desc);
                              setShowSavedDescriptionDropdown(false);
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{desc.name}</span>
                            </div>
                          </button>
                        </li>
                      ))
                  ) : savedDescriptions && savedDescriptions.length > 0 ? (
                    <li className="w-full">
                      <span className="text-base-content/70">
                        All descriptions already added
                      </span>
                    </li>
                  ) : (
                    <li className="w-full">
                      <span className="text-base-content/70">
                        No saved descriptions
                      </span>
                    </li>
                  )}
                  <li className="w-full border-t border-base-300 mt-2 pt-2">
                    <button
                      type="button"
                      className="text-left justify-start font-medium"
                      onClick={() => {
                        openModal();
                        setShowSavedDescriptionDropdown(false);
                      }}
                    >
                      <Plus size={16} />
                      Manage saved descriptions
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {showDescriptionInput && (
            <>
              {captions.length > 0 && (
                <div className="flex flex-col gap-2 mb-4">
                  {captions.map((caption, index) => (
                    <div key={index} className="card bg-base-200">
                      <div className="card-body p-3 sm:p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                        <p className="flex-1 text-sm sm:text-base">{caption}</p>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm w-full sm:w-auto"
                          onClick={() => {
                            setShowDescriptionInput(false);
                            setValue(fieldName, watch(fieldName) + caption);
                          }}
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="form-control mb-2">
                <input
                  placeholder="Optional: Add details about the photos"
                  className="input w-full text-sm"
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                />
              </div>

              <div>
                <button
                  type="button"
                  className="btn gap-2 btn-primary w-full sm:w-auto"
                  onClick={handleGenerateCaptions}
                  disabled={isGenerating}
                >
                  {isGenerating ? <Loader size={16} /> : <Zap size={16} />}
                  <span className="hidden sm:inline">
                    Generate AI Descriptions
                  </span>
                  <span className="sm:hidden">Generate AI</span>
                </button>
              </div>
            </>
          )}

          {editFeed && (
            <>
              <hr className="my-2" />
              <div className="flex">
                <button
                  className="btn btn-primary w-full sm:w-auto"
                  onClick={editFeed}
                >
                  Save Changes
                </button>
              </div>
            </>
          )}

          <SavedDescriptions
            open={open}
            close={closeModal}
            updateContent={updateContent}
            name={name}
            watch={watch}
            setValue={setValue}
            useModal={true}
          />
        </div>
      </div>
    </div>
  );
}

function formatSavedDescription(desc: SavedContent) {
  return `+[${desc.slug}](${desc.id})`;
}
